<template>
  <div
    class="accordion"
    :class="[
      type,
      `background-${headerBackground}`,
      `${roundedCorners === true ? 'rounded-md' : ''}`,
      `border-b-md border-solid ${borderColorClass}`,
      showBorderTop && `border-t-md border-solid ${borderColorClass}`,
      classes,
    ]"
  >
    <div
      tabindex="0"
      role="button"
      :class="[
        headerClasses,
        'pointer',
        'py-16',
        'md:py-24',
        'accordion-header',
        accordionHeaderRadiusClasses,
        ...props.titleCss,
        'hover:bg-surface-action-hover',
        'active:shadow-buttonSoft',
      ]"
      :aria-expanded="accordionIsOpen"
      :aria-controls="`${id}-content`"
      @click="toggle()"
      @keyup.enter.self.prevent="toggle()"
      @keyup.space.self.prevent="toggle()"
    >
      <div class="accordion-header-inner flex flex-row justify-between">
        <div>
          <slot :id="`${id}-label`" name="header"></slot>
        </div>

        <nuxt-icon
          role="presentation"
          class="flex flex-row items-center text-24 px-12 md:px-16 text-text-primary"
          :name="accordionIsOpen ? 'chevron-up' : 'chevron-down'"
          :alt="accordionIsOpen ? 'ikon pil uppåt' : 'ikon pil nedåt'"
        />
      </div>
    </div>
    <div
      :id="`${id}-content`"
      ref="contentSlotRef"
      class="overflow-hidden accordion-content"
      :class="{
        collapsing: isCollapsing && !accordionIsOpen,
        'collapsed invisible': !isCollapsing && !accordionIsOpen,
        'rounded-md rounded-t-none': roundedCorners,
      }"
      :aria-labelledby="`${id}-label`"
    >
      <slot></slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
interface Props {
  baseHtmlId?: string;
  isOpen?: boolean | false;
  type?: 'link' | 'link-no-border' | '';
  headerBackground?: 'default' | 'yellow';
  classes?: string;
  headerClasses?: string;
  roundedCorners?: boolean;
  borderColor?: BorderColorClassKey;
  showBorderTop?: boolean;
  titleCss?: string[];
}

const props = withDefaults(defineProps<Props>(), {
  baseHtmlId: '',
  isOpen: false,
  type: '',
  headerBackground: 'default',
  classes: '',
  headerClasses: '',
  roundedCorners: false,
  borderColor: 'tertiary',
  showBorderTop: false,
  titleCss: () => [],
});

const id = props.baseHtmlId || useId();

const emit = defineEmits(['onToggle']);
const { width } = useWindowSize();

// accordionIsOpen: Show/hide state
// isCollapsing: Necessary to know when hiding animation is complete, so
// visibility then can be turned off on the body for sight impaired users
const accordionIsOpen: Ref<boolean> = ref(props.isOpen);
const isCollapsing: Ref<boolean> = ref(false);

const contentSlotRef = useWatchSlotContentChange(onContentSlotElementsChange);

const bodyHeight = ref('999px');
const borderColorClass = useTWBorderColorClass(props.borderColor);

const accordionHeaderRadiusClasses: Ref<string> = computed(() => {
  if (props.roundedCorners) {
    return accordionIsOpen.value ? 'rounded-t-md' : 'rounded-md';
  }
  return '';
});

function toggle() {
  accordionIsOpen.value = !accordionIsOpen.value;
  onToggle();
}

function setOpen(value: boolean) {
  accordionIsOpen.value = value;
  onToggle();
}

function updateBodyMaxHeightForAnimation() {
  const panel = document && document.getElementById(`${id}-content`);
  if (panel) {
    bodyHeight.value = panel.scrollHeight + 'px';
  }
}

function updateCollapsingStateForAnimation() {
  if (accordionIsOpen.value === false) {
    isCollapsing.value = true;
    setTimeout(() => {
      isCollapsing.value = false;
    }, 300);
  }
}

function onToggle() {
  updateBodyMaxHeightForAnimation();
  updateCollapsingStateForAnimation();
  emit('onToggle', {
    isOpen: accordionIsOpen.value,
    id,
  });
}

function onContentSlotElementsChange() {
  updateBodyMaxHeightForAnimation();
}

watch(
  () => props.isOpen,
  (value: boolean) => {
    if (accordionIsOpen.value !== value) {
      setOpen(value);
    }
  },
);

onMounted(() => {
  updateBodyMaxHeightForAnimation();
});

watch(width, () => updateBodyMaxHeightForAnimation());
</script>

<style lang="scss" scoped>
.accordion {
  width: 100%;
  &.background-yellow {
    .accordion-header {
      background: $yellow-500;
    }
  }
  &.link {
    border-bottom: none;
    .accordion-header {
      //padding: 0;
      gap: 0.8rem;
      font-size: 2.4rem;
      color: $green-700;
      justify-content: flex-start;
      align-items: center;
    }
    .accordion-content {
      margin-left: 0;
      //padding-top: 1.6rem;
    }
  }
  &.link-no-border {
    border-bottom: none;
  }
  .accordion-header:focus {
    outline: none;
  }
  .accordion-header:focus > .accordion-header-inner {
    @apply border-md border-solid border-border-highlight rounded-xs;
  }
}

.accordion-content {
  transition: all 300ms ease;
  max-height: v-bind(bodyHeight);
}
.accordion-content.collapsing,
.accordion-content.collapsed {
  max-height: 0px;
}

/* reduced-motion */
@media (prefers-reduced-motion: reduce) {
  .accordion-content {
    transition: none;
  }
}
</style>
